import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ApiError,
  api,
  type AdminUserRow,
  type AllianceFeature,
  type AuthUser,
  type ServerRow,
  type TenantRow,
} from '@/lib/api';

const FEATURES: { key: AllianceFeature; label: string }[] = [{ key: 'transfers', label: 'Transfers' }];

const errMsg = (e: unknown) => (e instanceof ApiError ? e.message : 'Failed');

export default function Superadmin({ user }: { user: AuthUser }) {
  const serversQuery = useQuery({ queryKey: ['admin', 'servers'], queryFn: api.adminListServers });
  const tenantsQuery = useQuery({ queryKey: ['admin', 'tenants'], queryFn: api.adminListTenants });
  const servers = serversQuery.data ?? [];
  const tenants = tenantsQuery.data ?? [];

  return (
    <div className="space-y-8">
      <header>
        <h1 className="text-xl font-semibold">{user.isSuperadmin ? 'Superadmin' : 'Server admin'}</h1>
        <p className="text-sm text-slate-400">
          {user.isSuperadmin
            ? 'Servers, alliances, feature flags and accounts across all alliances.'
            : 'Create and manage alliances on your server.'}
        </p>
      </header>
      {user.isSuperadmin && <ServersSection servers={servers} />}
      <TenantsSection user={user} servers={servers} tenants={tenants} />
      {user.isSuperadmin && <UsersSection user={user} tenants={tenants} />}
      <Style />
    </div>
  );
}

// ---- Servers --------------------------------------------------------------

function ServersSection({ servers }: { servers: ServerRow[] }) {
  const qc = useQueryClient();
  const [number, setNumber] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['admin'] });
    qc.invalidateQueries({ queryKey: ['me'] });
  };
  const create = useMutation({
    mutationFn: () => api.adminCreateServer({ number: Number(number), name: name.trim() || null }),
    onSuccess: () => {
      invalidate();
      setNumber('');
      setName('');
      setError(null);
    },
    onError: (e) => setError(errMsg(e)),
  });
  const update = useMutation({
    mutationFn: (v: { id: number; number: number }) => api.adminUpdateServer(v.id, { number: v.number }),
    onSuccess: invalidate,
    onError: (e) => setError(errMsg(e)),
  });

  return (
    <section className="space-y-3">
      <h2 className="text-sm font-medium uppercase tracking-wider text-slate-400">Servers</h2>
      <div className="flex flex-wrap gap-2">
        {servers.map((s) => (
          <button
            key={s.id}
            title="Click to change the server number"
            onClick={() => {
              const v = prompt(`New number for server #${s.number}`, String(s.number));
              if (v != null && v.trim() !== '' && Number.isInteger(Number(v))) {
                update.mutate({ id: s.id, number: Number(v) });
              }
            }}
            className="rounded border border-slate-700 px-3 py-1 text-sm hover:bg-slate-800"
          >
            #{s.number}
            {s.name ? <span className="text-slate-500"> · {s.name}</span> : null}
          </button>
        ))}
      </div>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (number.trim()) create.mutate();
        }}
        className="flex flex-wrap gap-2"
      >
        <input
          className="input w-32"
          type="number"
          min={0}
          placeholder="Number"
          value={number}
          onChange={(e) => setNumber(e.target.value)}
          required
        />
        <input
          className="input w-56"
          placeholder="Label (optional)"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <button className="btn-primary" disabled={create.isPending}>
          Add server
        </button>
      </form>
      {error && <div className="text-sm text-red-400">{error}</div>}
    </section>
  );
}

// ---- Tenants --------------------------------------------------------------

function TenantsSection({
  user,
  servers,
  tenants,
}: {
  user: AuthUser;
  servers: ServerRow[];
  tenants: TenantRow[];
}) {
  return (
    <section className="space-y-3">
      <h2 className="text-sm font-medium uppercase tracking-wider text-slate-400">Alliances</h2>
      <CreateTenantForm servers={servers} />
      <div className="overflow-x-auto rounded-lg border border-slate-800">
        <table className="w-full min-w-[760px] text-sm">
          <thead className="bg-slate-900 text-left text-xs uppercase tracking-wider text-slate-400">
            <tr>
              <th className="px-3 py-2">Server</th>
              <th className="px-3 py-2">Alliance</th>
              <th className="px-3 py-2">Slug</th>
              <th className="px-3 py-2 text-right">Users</th>
              <th className="px-3 py-2 text-right">Players</th>
              <th className="px-3 py-2">Features</th>
              <th className="px-3 py-2 text-right">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-800">
            {tenants.map((t) => (
              <TenantRowView key={t.id} row={t} canFeatures={user.isSuperadmin} />
            ))}
            {tenants.length === 0 && (
              <tr>
                <td colSpan={7} className="px-3 py-6 text-center text-slate-500">
                  No alliances yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function TenantRowView({ row, canFeatures }: { row: TenantRow; canFeatures: boolean }) {
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['admin'] });
    qc.invalidateQueries({ queryKey: ['me'] });
  };
  const setFeatures = useMutation({
    mutationFn: (features: AllianceFeature[]) => api.adminSetTenantFeatures(row.id, features),
    onSuccess: invalidate,
    onError: (e) => setError(errMsg(e)),
  });
  const toggleActive = useMutation({
    mutationFn: () => api.adminUpdateTenant(row.id, { active: !row.active }),
    onSuccess: invalidate,
    onError: (e) => setError(errMsg(e)),
  });

  return (
    <tr className={row.active ? '' : 'opacity-60'}>
      <td className="px-3 py-2 text-slate-300">#{row.serverNumber ?? '—'}</td>
      <td className="px-3 py-2 font-medium">
        {row.name}
        {row.tag && <span className="ml-1 text-slate-500">[{row.tag}]</span>}
      </td>
      <td className="px-3 py-2 text-slate-400">{row.slug ?? '—'}</td>
      <td className="px-3 py-2 text-right tabular-nums">{row.userCount}</td>
      <td className="px-3 py-2 text-right tabular-nums">{row.playerCount}</td>
      <td className="px-3 py-2">
        <div className="flex flex-wrap gap-3">
          {FEATURES.map((f) => {
            const on = row.features.includes(f.key);
            return (
              <label key={f.key} className="inline-flex items-center gap-1.5 text-xs">
                <input
                  type="checkbox"
                  checked={on}
                  disabled={!canFeatures || setFeatures.isPending}
                  onChange={() =>
                    setFeatures.mutate(
                      on ? row.features.filter((x) => x !== f.key) : [...row.features, f.key],
                    )
                  }
                />
                {f.label}
              </label>
            );
          })}
        </div>
        {error && <div className="mt-1 text-xs text-red-400">{error}</div>}
      </td>
      <td className="px-3 py-2 text-right">
        <button
          onClick={() => {
            if (!row.active || confirm(`Deactivate ${row.name}? Its users can no longer sign in.`)) {
              toggleActive.mutate();
            }
          }}
          disabled={toggleActive.isPending}
          className={
            row.active
              ? 'rounded border border-amber-800 px-2 py-1 text-xs text-amber-300 hover:bg-amber-900/40'
              : 'rounded border border-emerald-800 px-2 py-1 text-xs text-emerald-300 hover:bg-emerald-900/40'
          }
        >
          {row.active ? 'Deactivate' : 'Reactivate'}
        </button>
      </td>
    </tr>
  );
}

function CreateTenantForm({ servers }: { servers: ServerRow[] }) {
  const qc = useQueryClient();
  const [serverId, setServerId] = useState<number | ''>('');
  const [name, setName] = useState('');
  const [tag, setTag] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const effectiveServer = serverId === '' ? servers[0]?.id : serverId;

  const create = useMutation({
    mutationFn: () =>
      api.adminCreateTenant({
        serverId: effectiveServer!,
        name: name.trim(),
        tag: tag.trim() || null,
        admin: { username: username.trim(), password },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin'] });
      setDone(`Created ${name.trim()} with R5 account "${username.trim()}".`);
      setName('');
      setTag('');
      setUsername('');
      setPassword('');
      setError(null);
    },
    onError: (e) => {
      setDone(null);
      setError(errMsg(e));
    },
  });

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (effectiveServer) create.mutate();
      }}
      className="space-y-3 rounded-lg border border-slate-800 bg-slate-900/40 p-4"
    >
      <h3 className="text-sm font-medium text-slate-300">New alliance + first R5 account</h3>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-5">
        <select
          className="input"
          value={effectiveServer ?? ''}
          onChange={(e) => setServerId(Number(e.target.value))}
          required
        >
          {servers.map((s) => (
            <option key={s.id} value={s.id}>
              Server #{s.number}
            </option>
          ))}
        </select>
        <input
          className="input"
          placeholder="Alliance name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
        />
        <input
          className="input"
          placeholder="Tag"
          value={tag}
          onChange={(e) => setTag(e.target.value)}
          maxLength={16}
        />
        <input
          className="input"
          placeholder="R5 username"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          minLength={3}
          required
        />
        <input
          className="input"
          type="password"
          placeholder="Initial password (min 8)"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          minLength={8}
          required
        />
      </div>
      <button className="btn-primary" disabled={create.isPending || !effectiveServer}>
        {create.isPending ? 'Creating…' : 'Create alliance'}
      </button>
      {done && <div className="text-sm text-emerald-400">{done}</div>}
      {error && <div className="text-sm text-red-400">{error}</div>}
    </form>
  );
}

// ---- Users ----------------------------------------------------------------

function UsersSection({ user, tenants }: { user: AuthUser; tenants: TenantRow[] }) {
  const usersQuery = useQuery({ queryKey: ['admin', 'users'], queryFn: api.adminListUsers });
  const [filter, setFilter] = useState('');
  const rows = (usersQuery.data ?? []).filter((u) => {
    const q = filter.trim().toLowerCase();
    if (!q) return true;
    return (
      u.username.toLowerCase().includes(q) ||
      (u.allianceName ?? '').toLowerCase().includes(q) ||
      (u.allianceTag ?? '').toLowerCase().includes(q)
    );
  });

  return (
    <section className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-sm font-medium uppercase tracking-wider text-slate-400">Accounts</h2>
        <input
          className="input w-64"
          placeholder="Filter by user or alliance"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
        />
      </div>
      <div className="overflow-x-auto rounded-lg border border-slate-800">
        <table className="w-full min-w-[760px] text-sm">
          <thead className="bg-slate-900 text-left text-xs uppercase tracking-wider text-slate-400">
            <tr>
              <th className="px-3 py-2">User</th>
              <th className="px-3 py-2">Alliance</th>
              <th className="px-3 py-2">Role</th>
              <th className="px-3 py-2">Server admin</th>
              <th className="px-3 py-2">Superadmin</th>
              <th className="px-3 py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-800">
            {rows.map((u) => (
              <UserRowView key={u.id} row={u} self={u.id === user.id} tenants={tenants} />
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function UserRowView({
  row,
  self,
  tenants,
}: {
  row: AdminUserRow;
  self: boolean;
  tenants: TenantRow[];
}) {
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const update = useMutation({
    mutationFn: (body: Parameters<typeof api.adminUpdateUser>[1]) => api.adminUpdateUser(row.id, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'users'] }),
    onError: (e) => setError(errMsg(e)),
  });
  const impersonate = useMutation({
    mutationFn: () => api.adminImpersonate(row.id),
    onSuccess: () => {
      qc.clear();
      window.location.assign('/dashboard');
    },
    onError: (e) => setError(errMsg(e)),
  });

  return (
    <tr className={row.active ? '' : 'opacity-60'}>
      <td className="px-3 py-2 font-medium">
        {row.username}
        {self && <span className="ml-1 text-xs text-slate-500">(you)</span>}
      </td>
      <td className="px-3 py-2">
        <select
          className="input"
          value={row.allianceId ?? ''}
          disabled={update.isPending}
          onChange={(e) => {
            const v = e.target.value === '' ? null : Number(e.target.value);
            if (confirm(`Move ${row.username} to another alliance? Their player link is cleared.`)) {
              update.mutate({ allianceId: v });
            }
          }}
        >
          <option value="">— none —</option>
          {tenants.map((t) => (
            <option key={t.id} value={t.id}>
              #{t.serverNumber} {t.tag || t.name}
            </option>
          ))}
        </select>
      </td>
      <td className="px-3 py-2 text-slate-300">{row.role}</td>
      <td className="px-3 py-2">
        <input
          type="checkbox"
          checked={row.isServerAdmin}
          disabled={update.isPending}
          onChange={() => update.mutate({ isServerAdmin: !row.isServerAdmin })}
        />
      </td>
      <td className="px-3 py-2">
        <input
          type="checkbox"
          checked={row.isSuperadmin}
          disabled={self || update.isPending}
          onChange={() => {
            if (row.isSuperadmin || confirm(`Grant superadmin to ${row.username}?`)) {
              update.mutate({ isSuperadmin: !row.isSuperadmin });
            }
          }}
        />
      </td>
      <td className="px-3 py-2 text-right">
        {!self && row.active && (
          <button
            onClick={() => impersonate.mutate()}
            disabled={impersonate.isPending}
            className="rounded border border-amber-800 px-2 py-1 text-xs text-amber-300 hover:bg-amber-900/40"
          >
            Impersonate
          </button>
        )}
        {error && <div className="mt-1 text-xs text-red-400">{error}</div>}
      </td>
    </tr>
  );
}

function Style() {
  return (
    <style>{`
      .input {
        background: rgb(2 6 23 / 1);
        border: 1px solid rgb(51 65 85 / 1);
        border-radius: 6px;
        padding: 6px 10px;
        font-size: 14px;
        color: inherit;
        width: 100%;
      }
      .input:focus { outline: none; border-color: rgb(100 116 139 / 1); }
      .btn-primary {
        background: rgb(79 70 229 / 1);
        color: white;
        padding: 6px 12px;
        font-size: 14px;
        border-radius: 6px;
      }
      .btn-primary:hover { background: rgb(99 102 241 / 1); }
      .btn-primary:disabled { opacity: 0.5; }
    `}</style>
  );
}
